'use client';

import { Check, Copy, Play } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import A2UIMessageRenderer from '@/components/custom/chat/chat-panel/A2UIMessageRenderer';
import { deriveA2UIPreviewRendererInput, formatA2UIPreviewJson, getA2UIPreviewObjectsJson, getA2UIPreviewPlaybackChunks, parseA2UIPreviewCode } from '@macaron-a2ui/core/v0_8';
import ModernMonacoEditor from './ModernMonacoEditor';
import { A2UI_PREVIEW_SAMPLE_INPUT } from './a2uiPreviewShared';
import { useCodeTokenCount } from './useCodeTokenCount';

export { deriveA2UIPreviewRendererInput, formatA2UIPreviewJson, getA2UIPreviewObjectsJson, getA2UIPreviewPlaybackChunks, parseA2UIPreviewCode };
export type A2UIPreviewCopy = {
  emptyState: string;
  invalidJson: string;
  parseError: string;
  copyObjects: string;
  copiedObjects: string;
};
type A2UIPreviewLayoutVariant = 'standalone' | 'embedded';

const TOKEN_COUNT_FORMATTER = new Intl.NumberFormat('en-US');

export default function A2UIPreviewPage({
  initialRaw = A2UI_PREVIEW_SAMPLE_INPUT,
  monacoSsrHtml,
  copy,
  layout = 'standalone',
  className = '',
  documentKey = 'preview:default',
}: {
  initialRaw?: string;
  monacoSsrHtml?: string;
  copy: A2UIPreviewCopy;
  layout?: A2UIPreviewLayoutVariant;
  className?: string;
  documentKey?: string;
}) {
  const [raw, setRaw] = useState(initialRaw);
  const [isPlaying, setIsPlaying] = useState(false);
  const [didCopyObjects, setDidCopyObjects] = useState(false);
  const playbackChunksRef = useRef<string[]>([]);
  const playbackIndexRef = useRef(0);
  const lastAppliedPlaybackValueRef = useRef<string | null>(null);
  const playbackAnimationFrameRef = useRef<number | null>(null);
  const copyResetTimeoutRef = useRef<number | null>(null);
  const rawRef = useRef(raw);
  const tokenCount = useCodeTokenCount(raw);
  const parsed = useMemo(() => parseA2UIPreviewCode(raw, copy.invalidJson), [copy.invalidJson, raw]);
  const rendererInput = useMemo(() => deriveA2UIPreviewRendererInput(parsed.value), [parsed.value]);
  const objectsJson = useMemo(() => getA2UIPreviewObjectsJson(parsed.value), [parsed.value]);
  const canFormat = !isPlaying && raw.trim().length > 0 && !parsed.error && !parsed.partial;
  const canPlay = !isPlaying && raw.length > 0;
  const canCopyObjects = objectsJson !== null;
  const embedded = layout === 'embedded';

  const clearPlaybackAnimationFrame = () => {
    if (playbackAnimationFrameRef.current === null) return;
    window.cancelAnimationFrame(playbackAnimationFrameRef.current);
    playbackAnimationFrameRef.current = null;
  };

  const clearCopyResetTimeout = () => {
    if (copyResetTimeoutRef.current === null) return;
    window.clearTimeout(copyResetTimeoutRef.current);
    copyResetTimeoutRef.current = null;
  };

  const stopPlayback = () => {
    clearPlaybackAnimationFrame();
    playbackChunksRef.current = [];
    playbackIndexRef.current = 0;
    lastAppliedPlaybackValueRef.current = null;
    setIsPlaying(false);
  };

  useEffect(
    () => () => {
      clearPlaybackAnimationFrame();
      clearCopyResetTimeout();
    },
    [],
  );

  useEffect(() => {
    stopPlayback();
    clearCopyResetTimeout();
    setDidCopyObjects(false);
    setRaw(initialRaw);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This reset is intentionally keyed only by the incoming seed value; the helpers here only touch refs/local state and should not retrigger the reset on every render.
  }, [initialRaw]);

  rawRef.current = raw;

  const appendNextPlaybackChunk = () => {
    const nextChunk = playbackChunksRef.current[playbackIndexRef.current];
    if (typeof nextChunk !== 'string') return stopPlayback();
    playbackIndexRef.current += 1;
    setRaw((previous) => previous + nextChunk);
  };

  const scheduleNextPlaybackChunk = () => {
    if (playbackAnimationFrameRef.current !== null) return;
    playbackAnimationFrameRef.current = window.requestAnimationFrame(() => {
      playbackAnimationFrameRef.current = null;
      appendNextPlaybackChunk();
    });
  };

  const handlePlaybackValueApplied = (appliedValue: string) => {
    if (!isPlaying || appliedValue !== rawRef.current || appliedValue === lastAppliedPlaybackValueRef.current) return;
    lastAppliedPlaybackValueRef.current = appliedValue;
    if (playbackIndexRef.current >= playbackChunksRef.current.length) return stopPlayback();
    scheduleNextPlaybackChunk();
  };

  const handlePlay = () => {
    if (!canPlay) return;
    const chunks = getA2UIPreviewPlaybackChunks(raw);
    if (!chunks.length) return;
    playbackChunksRef.current = chunks;
    playbackIndexRef.current = 0;
    lastAppliedPlaybackValueRef.current = null;
    setRaw('');
    setIsPlaying(true);
  };

  const handleFormat = () => {
    if (!canFormat) return;
    setRaw(formatA2UIPreviewJson(parsed.value));
  };

  const handleCopyObjects = async () => {
    if (!objectsJson) return;
    const clipboard = navigator.clipboard;
    if (!clipboard?.writeText) return;
    try {
      await clipboard.writeText(objectsJson);
      setDidCopyObjects(true);
      clearCopyResetTimeout();
      copyResetTimeoutRef.current = window.setTimeout(() => {
        copyResetTimeoutRef.current = null;
        setDidCopyObjects(false);
      }, 1500);
    } catch (error) {
      console.error('Failed to copy A2UI preview objects:', error);
    }
  };

  const content = (
    <section className={`a2ui-preview-grid grid h-full min-h-0 grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:gap-4 ${className}`.trim()}>
      <div className="flex min-h-0 flex-col overflow-hidden rounded-[10px] border border-[#e7ddd2] bg-white shadow-[0_1px_2px_rgba(32,24,12,0.04)]">
        <div className="min-h-0 flex-1 overflow-hidden">
          <ModernMonacoEditor value={raw} onChange={setRaw} ssrHtml={monacoSsrHtml} readOnly={isPlaying} onExternalValueApplied={handlePlaybackValueApplied} documentKey={documentKey} />
        </div>
        <div className="border-t border-[#e7ddd3] bg-[linear-gradient(90deg,rgba(255,255,255,0.96)_0%,rgba(255,255,255,0.72)_36%,rgba(255,255,255,0)_78%)] px-3 md:px-4">
          <div className="flex h-12 shrink-0 items-center justify-between gap-3 text-[13px] text-[#7b7065]" aria-live="polite">
            <span className="tabular-nums whitespace-pre text-[#4f463f]">{TOKEN_COUNT_FORMATTER.format(tokenCount ?? 0)} tokens</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#dfd4c8] bg-[#fbf7f0] text-[#5d5349] transition hover:border-[#cdbca9] hover:bg-[#f5efe4] disabled:cursor-not-allowed disabled:border-[#ece2d8] disabled:bg-[#f7f1e8] disabled:text-[#b0a497]"
                onClick={handleCopyObjects}
                disabled={!canCopyObjects}
                aria-label={didCopyObjects ? copy.copiedObjects : copy.copyObjects}
              >
                {didCopyObjects ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                className="inline-flex h-8 items-center justify-center rounded-full border border-[#dfd4c8] bg-[#fbf7f0] px-3 text-[12px] font-medium text-[#5d5349] transition hover:border-[#cdbca9] hover:bg-[#f5efe4] disabled:cursor-not-allowed disabled:border-[#ece2d8] disabled:bg-[#f7f1e8] disabled:text-[#b0a497]"
                onClick={handleFormat}
                disabled={!canFormat}
                aria-label="Format A2UI preview code"
              >
                Format
              </button>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#dfd4c8] bg-[#fbf7f0] text-[#5d5349] transition hover:border-[#cdbca9] hover:bg-[#f5efe4] disabled:cursor-not-allowed disabled:border-[#ece2d8] disabled:bg-[#f7f1e8] disabled:text-[#b0a497]"
                onClick={handlePlay}
                disabled={!canPlay}
                aria-label={isPlaying ? 'Playing A2UI preview code' : 'Play A2UI preview code'}
              >
                <Play className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex min-h-0 flex-col">
        <div className="-mx-3 -mt-3 flex-1 overflow-auto px-3 pt-3 md:-mx-4 md:-mt-4 md:px-4 md:pt-4">
          {parsed.error ? (
            <div className="border border-[#f0c7c7] bg-[#fff2f2] px-4 py-3 text-[13px] leading-6 text-[#9f3131]">{copy.parseError.replace('{message}', parsed.error)}</div>
          ) : rendererInput?.messages?.length || rendererInput?.canvasPayload ? (
            <div className="a2ui-preview-surface">
              <A2UIMessageRenderer
                surfaceId={rendererInput.surfaceId}
                surfaceVersion={rendererInput.surfaceVersion}
                originMessageId={rendererInput.originMessageId}
                messages={rendererInput.messages}
                canvasPayload={rendererInput.canvasPayload}
              />
            </div>
          ) : (
            <div className="rounded-[10px] border border-dashed border-[#d9ccbf] bg-white px-4 py-10 text-[14px] leading-6 text-black/45 shadow-[0_1px_2px_rgba(32,24,12,0.04)]">{copy.emptyState}</div>
          )}
        </div>
      </div>
    </section>
  );
  if (embedded) return content;
  return <main className="h-screen overflow-hidden bg-[#f6f3ec] px-3 py-3 md:px-4 md:py-4">{content}</main>;
}
